// src/components/CustomerDashboard/ManualRefillReminders.js
//
// Per-customer tab for CRM-only customers (gf/models.py::OrganizationSettings.crm_only).
// Mirrors the existing RefillReminders.js tab component's shape, but backed
// by ManualRefillReminder — includes an inline create form since there's no
// Invoice/Prescription this data can be derived from.

import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Tag } from "primereact/tag";
import { format } from "date-fns";
import axiosInstance from "../../services/axiosInstance";
import { handleApiError, handleApiSuccess } from "../apiErrorHandler";

const emptyForm = { medication_name: "", refill_date: null, reminder_date: null };

const ManualRefillReminders = ({ customerId }) => {
    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [sendingSms, setSendingSms] = useState({});

    const fetchReminders = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/api/manual-refill-reminders/`, { params: { customer_id: customerId } });
            setReminders(response.data.results);
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReminders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    const handleSave = async () => {
        if (!form.medication_name.trim() || !form.refill_date || !form.reminder_date) {
            handleApiError({ Error: "Medication name, refill date, and reminder date are all required." });
            return;
        }
        setSaving(true);
        try {
            const res = await axiosInstance.post('/api/manual-refill-reminders/', {
                customer: customerId,
                medication_name: form.medication_name.trim(),
                refill_date: format(new Date(form.refill_date), 'yyyy-MM-dd'),
                reminder_date: format(new Date(form.reminder_date), 'yyyy-MM-dd'),
            });
            setReminders(prev => [res.data, ...prev]);
            handleApiSuccess('Refill reminder created.');
            setForm(emptyForm);
            setShowForm(false);
        } catch (error) {
            handleApiError(error);
        } finally {
            setSaving(false);
        }
    };

    const handleSendSms = async (reminder) => {
        if (sendingSms[reminder.id]) return;
        setSendingSms(prev => ({ ...prev, [reminder.id]: true }));
        try {
            await axiosInstance.post(`/api/manual-refill-reminders/${reminder.id}/send-sms/`);
            handleApiSuccess('SMS reminder sent.');
            setReminders(prev => prev.map(r =>
                r.id === reminder.id ? { ...r, sms_sent_at: new Date().toISOString(), status: 'Completed' } : r
            ));
        } catch (error) {
            handleApiError(error);
        } finally {
            setSendingSms(prev => ({ ...prev, [reminder.id]: false }));
        }
    };

    return (
        <div className="p-4 rounded-lg shadow">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="text-lg font-semibold">Manual Refill Reminders</h3>
                <Button
                    icon={showForm ? "pi pi-times" : "pi pi-plus"}
                    label={showForm ? "Cancel" : "New Reminder"}
                    className="p-button-sm p-button-text"
                    onClick={() => setShowForm(v => !v)}
                />
            </div>

            {showForm && (
                <div className="formgrid grid mt-2 mb-3">
                    <div className="field col-12">
                        <label className="font-bold">Medication Name:</label>
                        <InputText
                            value={form.medication_name}
                            onChange={(e) => setForm(f => ({ ...f, medication_name: e.target.value }))}
                            placeholder="e.g. Amlodipine 5mg"
                        />
                    </div>
                    <div className="field col">
                        <label className="font-bold">Refill Date:</label>
                        <Calendar
                            value={form.refill_date}
                            onChange={(e) => setForm(f => ({ ...f, refill_date: e.value }))}
                            showIcon
                        />
                    </div>
                    <div className="field col">
                        <label className="font-bold">Reminder Date:</label>
                        <Calendar
                            value={form.reminder_date}
                            onChange={(e) => setForm(f => ({ ...f, reminder_date: e.value }))}
                            showIcon
                        />
                    </div>
                    <div className="field col-12" style={{ textAlign: 'right' }}>
                        <Button label="Save" icon="pi pi-check" onClick={handleSave} loading={saving} />
                    </div>
                </div>
            )}

            {loading ? (
                <p>Loading reminders...</p>
            ) : reminders.length === 0 ? (
                <p>No manual refill reminders yet.</p>
            ) : (
                <ul style={{ listStyle: 'none', padding: 0 }}>
                    {reminders.map((reminder) => {
                        const alreadySent = !!reminder.sms_sent_at;
                        const isLoading = !!sendingSms[reminder.id];
                        return (
                            <li key={reminder.id} className="border-b p-2" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <div>{reminder.medication_name} — refill on {reminder.refill_date}</div>
                                    <Tag
                                        value={reminder.status}
                                        severity={reminder.status === 'Completed' ? 'success' : reminder.status === 'Missed' ? 'danger' : 'warning'}
                                        style={{ marginTop: 4 }}
                                    />
                                </div>
                                <Button
                                    icon={alreadySent ? 'pi pi-check' : 'pi pi-send'}
                                    label={alreadySent ? 'Sent' : 'Send SMS'}
                                    className={`p-button-sm ${alreadySent ? 'p-button-success p-button-outlined' : 'p-button-outlined'}`}
                                    disabled={alreadySent || isLoading}
                                    loading={isLoading}
                                    onClick={() => handleSendSms(reminder)}
                                />
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default ManualRefillReminders;